/**
 * Block Map – view script
 *
 * Loads the Google Maps JavaScript API and renders:
 * - An interactive map with AdvancedMarkerElement for every retailer.
 * - A retailer list that lets the user filter retailers by ZIP / city and radius.
 *
 * The Google Maps JS API is registered server-side (EnqueueStylesAndScripts.php)
 * with the API key embedded in the script URL — it is never exposed in this file.
 * Geocoding is proxied through the WordPress REST endpoint `wombat/v1/geocode`
 * so the API key is never sent to the browser at all.
 */

import { getParameters } from '../../js/front/analytics';

/** Size in px of one declutter cell on the map; a single pin is kept per cell. */
const PIN_CELL_PX = 48;
/** From this zoom up every filtered pin inside the viewport is shown. */
const FULL_DETAIL_ZOOM = 15;
/** Share of the viewport added on each side when choosing which pins to render. */
const VIEWPORT_PADDING = 0.1;
/** Metres in one mile, for Circle radius. */
const METERS_PER_MILE = 1609.344;

const MAP_STYLES = [
	// Map styling to shades of gray
	{
		elementType: "geometry",
		stylers: [{ color: "#e0e0e0" }]
	},
	{
		elementType: "labels.icon",
		stylers: [{ saturation: -100 }, { lightness: 10 }]
	},
	{
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		elementType: "labels.text.stroke",
		stylers: [{ color: "#f5f5f5" }]
	},
	// Styling for administrative boundaries
	{
		featureType: "administrative",
		elementType: "geometry",
		stylers: [{ color: "#cfcfcf" }]
	},
	{
		featureType: "administrative.land_parcel",
		elementType: "labels.text.fill",
		stylers: [{ color: "#bdbdbd" }]
	},
	// Styling for points of interest (POI)
	{
		featureType: "poi",
		elementType: "geometry",
		stylers: [{ color: "#d6d6d6" }]
	},
	{
		featureType: "poi",
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		featureType: "poi.business",
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		featureType: "poi.government",
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		featureType: "poi.school",
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		featureType: "poi.park",
		elementType: "geometry",
		stylers: [{ color: "#cfcfcf" }]
	},
	{
		featureType: "poi.park",
		elementType: "labels.text.fill",
		stylers: [{ color: "#9e9e9e" }]
	},
	// Styling for roads
	{
		featureType: "road",
		elementType: "geometry",
		stylers: [{ color: "#ffffff" }]
	},
	{
		featureType: "road.arterial",
		elementType: "labels.text.fill",
		stylers: [{ color: "#757575" }]
	},
	{
		featureType: "road.highway",
		elementType: "geometry",
		stylers: [{ color: "#bdbdbd" }]
	},
	{
		featureType: "road.highway",
		elementType: "labels.text.fill",
		stylers: [{ color: "#616161" }]
	},
	{
		featureType: "road.local",
		elementType: "labels.text.fill",
		stylers: [{ color: "#9e9e9e" }]
	},
	// Styling for transit
	{
		featureType: "transit.line",
		elementType: "geometry",
		stylers: [{ color: "#e5e5e5" }]
	},
	{
		featureType: "transit.station",
		elementType: "geometry",
		stylers: [{ color: "#eeeeee" }]
	},
	// Styling for water bodies
	{
		featureType: "water",
		elementType: "geometry",
		stylers: [{ color: "#bdbdbd" }]
	},
	{
		featureType: "water",
		elementType: "labels.text.fill",
		stylers: [{ color: "#9e9e9e" }]
	}
];

// ─── Google Maps bootstrap ────────────────────────────────────────────────────

/**
 * Return a Promise that resolves once the Google Maps JavaScript API bootstrap
 * is ready (i.e. `google.maps.importLibrary` is available).
 *
 * The Google Maps script is registered and loaded by WordPress (server-side,
 * see EnqueueStylesAndScripts.php). It is configured with
 * `callback=__googleMapsBootstrapReady`; the inline bootstrap script (added
 * `before` the Maps script tag) defines that callback and exposes two globals:
 * `window.__googleMapsBootstrapIsReady` – true once the callback fired.
 * `window.__googleMapsBootstrapCallbacks` – queue filled before the callback.
 *
 * This function handles the race between the Maps callback and this script.
 *
 * @return {Promise<void>}
 */
function waitForGoogleMaps() {

	if ( window.__googleMapsBootstrapPromise ) {

		return window.__googleMapsBootstrapPromise;

	}

	window.__googleMapsBootstrapPromise = new Promise( ( resolve ) => {

		// Callback already fired before this script ran.
		if ( window.__googleMapsBootstrapIsReady ) {

			resolve();
			return;

		}

		// Register for when the callback fires later.
		window.__googleMapsBootstrapCallbacks = window.__googleMapsBootstrapCallbacks || [];
		window.__googleMapsBootstrapCallbacks.push( resolve );

	} );

	return window.__googleMapsBootstrapPromise;

}

// ─── Geo helpers ─────────────────────────────────────────────────────────────

/**
 * Haversine great-circle distance in miles.
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @return {number}
 */
function haversineDistanceMiles( lat1, lng1, lat2, lng2 ) {

	const R      = 3959;
	const dLat   = ( lat2 - lat1 ) * Math.PI / 180;
	const dLng   = ( lng2 - lng1 ) * Math.PI / 180;
	const a      = Math.sin( dLat / 2 ) ** 2
		+ Math.cos( lat1 * Math.PI / 180 ) * Math.cos( lat2 * Math.PI / 180 )
		* Math.sin( dLng / 2 ) ** 2;

	return R * 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

}

/**
 * Geocode a free-text address via the WordPress REST proxy endpoint
 * (`wombat/v1/map-geocode`). The proxy makes the request server-side,
 * caches results (object cache + transients) and never exposes the API key.
 *
 * @param {string} address ZIP code, city name, or full address.
 * @return {Promise<{lat: number, lng: number}>}
 */
async function geocodeAddress( address ) {

	const geocodeUrl = window.wombatGeocodeUrl || '/wp-json/wombat/v1/map-geocode';
	const url        = `${ geocodeUrl }?q=${ encodeURIComponent( address ) }`;

	const response = await fetch( url, { credentials: 'same-origin' } );

	if ( ! response.ok ) {

		throw new Error( `Geocode failed (${ response.status })` );

	}

	/** @type {{ lat: number, lng: number }} */
	return response.json();

}

/**
 * Filter and sort retailers by distance from a centre point.
 *
 * @param {Array}  retailers   Full retailer list.
 * @param {{lat: number, lng: number}} center
 * @param {number} radiusMiles
 * @return {Array} All retailers in range, sorted by distance, each with a `distance` property.
 */
function filterAndSortRetailers( retailers, center, radiusMiles ) {

	return retailers
		.map( r => ( {
			...r,
			distance: haversineDistanceMiles(
				center.lat, center.lng,
				Number( r.latitude ), Number( r.longitude )
			),
		} ) )
		.filter( r => r.distance <= radiusMiles )
		.sort( ( a, b ) => a.distance - b.distance );

}

// ─── DOM / HTML helpers ───────────────────────────────────────────────────────

/**
 * Safely escape a string for use inside innerHTML.
 *
 * @param {string} str
 * @return {string}
 */
function escapeHtml( str ) {

	const div = document.createElement( 'div' );
	div.appendChild( document.createTextNode( str || '' ) );
	return div.innerHTML;

}

/**
 * Modifier class that shrinks the pin text for 3+ digit numbers.
 *
 * @param {string} baseClass
 * @param {number} number
 * @return {string} Leading-space modifier class or an empty string.
 */
function digitsModifier( baseClass, number ) {

	const digits = String( number ).length;

	return digits >= 3 ? ` ${ baseClass }--digits-${ Math.min( digits, 4 ) }` : '';

}

/**
 * Build a numbered pin element for AdvancedMarkerElement content.
 *
 * @param {number} number Position in the retailer list (1-based).
 * @return {HTMLElement}
 */
function buildNumberPinElement( number ) {

	const div            = document.createElement( 'div' );
	div.className        = `b-map__pin${ digitsModifier( 'b-map__pin', number ) }`;

	const span           = document.createElement( 'span' );
	span.className       = 'b-map__pin-number';
	span.textContent     = String( number );

	div.appendChild( span );
	return div;

}

/**
 * Label font size for classic markers, smaller for longer numbers.
 *
 * @param {number} number
 * @return {string}
 */
function labelFontSize( number ) {

	const digits = String( number ).length;

	if ( digits >= 4 ) {

		return '9px';

	}

	return digits === 3 ? '11px' : '14px';

}

// ─── Map helpers ─────────────────────────────────────────────────────────────

/**
 * Return a stable string key for a retailer object used as a Map index.
 *
 * Prefers `retailer.id` when available; falls back to a composite of
 * name + coordinates so existing data without an id still works.
 *
 * @param {{ id?: string|number, name: string, latitude: string|number, longitude: string|number }} retailer
 * @return {string}
 */
function retailerKey( retailer ) {

	return retailer.id !== null && retailer.id !== undefined
		? String( retailer.id )
		: `${ retailer.name }|${ retailer.latitude }|${ retailer.longitude }`;

}

/**
 * Fit the map viewport to the circle of the given radius around a centre point.
 *
 * @param {google.maps.Map}        map
 * @param {{lat: number, lng: number}} center
 * @param {number}                 radiusMiles
 */
function fitMapToRadius( map, center, radiusMiles ) {

	const circle = new window.google.maps.Circle( {
		center,
		radius: radiusMiles * METERS_PER_MILE,
	} );

	map.fitBounds( circle.getBounds() );

}

/**
 * Choose which of the filtered retailers get a pin on the map.
 *
 * Below FULL_DETAIL_ZOOM one pin is kept per PIN_CELL_PX screen cell (the one
 * with the lowest number). Cells are anchored to world coordinates, so the
 * selection stays stable while panning. Only pins inside the padded viewport
 * are returned. The pinned retailer is always included.
 *
 * @param {Array}           retailers  Filtered + sorted retailers.
 * @param {google.maps.Map} map
 * @param {string|null}     pinnedKey  Key of the retailer that must stay visible.
 * @return {number[]|null} Indexes into `retailers`, or null while the map is not ready.
 */
function selectVisibleIndexes( retailers, map, pinnedKey ) {

	const projection = map.getProjection();
	const bounds     = map.getBounds();

	if ( ! projection || ! bounds ) {

		return null;

	}

	const zoom      = map.getZoom();
	const scale     = 2 ** zoom;
	const declutter = zoom < FULL_DETAIL_ZOOM;
	const ne        = bounds.getNorthEast();
	const sw        = bounds.getSouthWest();
	const latPad    = ( ne.lat() - sw.lat() ) * VIEWPORT_PADDING;
	const lngPad    = ( ne.lng() - sw.lng() ) * VIEWPORT_PADDING;
	const occupied  = new Set();
	const visible   = [];

	retailers.forEach( ( retailer, i ) => {

		const lat    = Number( retailer.latitude );
		const lng    = Number( retailer.longitude );
		const pinned = pinnedKey !== null && retailerKey( retailer ) === pinnedKey;

		if ( declutter ) {

			const point = projection.fromLatLngToPoint( { lat, lng } );
			const cell  = `${ Math.floor( point.x * scale / PIN_CELL_PX ) }:${ Math.floor( point.y * scale / PIN_CELL_PX ) }`;

			if ( occupied.has( cell ) ) {

				if ( ! pinned ) {

					return;

				}

			} else {

				occupied.add( cell );

			}

		}

		const inView = lat <= ne.lat() + latPad && lat >= sw.lat() - latPad
			&& lng <= ne.lng() + lngPad && lng >= sw.lng() - lngPad;

		if ( inView || pinned ) {

			visible.push( i );

		}

	} );

	return visible;

}

/**
 * Put a numbered pin on a marker and attach it to the map.
 *
 * @param {object}          marker
 * @param {number}          number     Position in the retailer list (1-based).
 * @param {google.maps.Map} map
 * @param {string}          markerUrl
 */
function showMarkerWithNumber( marker, number, map, markerUrl ) {

	if ( typeof marker.setLabel === 'function' ) {

		if ( markerUrl ) {

			marker.setIcon( {
				url:         markerUrl,
				scaledSize:  new window.google.maps.Size( 26, 36 ),
				labelOrigin: new window.google.maps.Point( 13, 13 ),
			} );

		}

		marker.setLabel( {
			text:       String( number ),
			color:      '#000000',
			fontWeight: '700',
			fontSize:   labelFontSize( number ),
			fontFamily: getComputedStyle( document.documentElement ).getPropertyValue( '--wp--preset--font-family--accent' ).trim() || 'sans-serif',
		} );
		marker.setMap( map );

	} else {

		marker.content = buildNumberPinElement( number );
		marker.map     = map;

	}

}

/**
 * Detach a marker from the map.
 *
 * @param {object} marker
 */
function hideMarker( marker ) {

	if ( typeof marker.setMap === 'function' ) {

		marker.setMap( null );

	} else {

		marker.map = null;

	}

}

/**
 * Sync map markers with the current filtered set and viewport.
 * Only markers whose number changed are touched, so pins do not flicker.
 *
 * @param {{filtered: Array, pinnedKey: string|null}} state
 * @param {Map<string, {retailer: object, marker: object, number: number|null}>} markerByKey
 * @param {google.maps.Map} map
 * @param {string}          markerUrl
 */
function syncMarkers( state, markerByKey, map, markerUrl ) {

	const visible = selectVisibleIndexes( state.filtered, map, state.pinnedKey );

	// The map projection is not ready yet; the first `idle` event retries.
	if ( ! visible ) {

		return;

	}

	const numberByKey = new Map( visible.map( i => [ retailerKey( state.filtered[ i ] ), i + 1 ] ) );

	markerByKey.forEach( ( mo, key ) => {

		const number = numberByKey.get( key ) ?? null;

		if ( number === mo.number ) {

			return;

		}

		if ( number === null ) {

			hideMarker( mo.marker );

		} else {

			showMarkerWithNumber( mo.marker, number, map, markerUrl );

		}

		mo.number = number;

	} );

}

// ─── Retailer list rendering ───────────────────────────────────────────────────

/**
 * Store a new filtered set, render the retailer list and update the map pins.
 *
 * @param {{filtered: Array, indexByKey: Map<string, number>, pinnedKey: string|null}} state
 * @param {Array}           filtered     Filtered + sorted retailers (with distance).
 * @param {Map<string, {retailer: object, marker: object, number: number|null}>} markerByKey
 * @param {google.maps.Map} map
 * @param {string}          markerUrl
 * @param {HTMLElement|null} listEl
 * @param {Function}        onItemClick  Passed through to renderList.
 */
function applyResults( state, filtered, markerByKey, map, markerUrl, listEl, onItemClick ) {

	state.filtered   = filtered;
	state.indexByKey = new Map( filtered.map( ( r, i ) => [ retailerKey( r ), i ] ) );

	syncMarkers( state, markerByKey, map, markerUrl );

	if ( listEl ) {

		renderList( listEl, filtered, onItemClick );

	}

}

/**
 * Show retailers within a given radius from a centre point: numbered pins on
 * the map and the full matching set in the list.
 * Used on initial load and on search reset.
 *
 * @param {Array}           allRetailers   Full retailer list from the block data.
 * @param {{lat:number,lng:number}} center Centre point (defaults to map centre).
 * @param {number}          radiusMiles    Filter radius in miles.
 * @param {object}          state          Shared filtered-set state.
 * @param {Map<string, {retailer: object, marker: object, number: number|null}>} markerByKey O(1) marker index.
 * @param {google.maps.Map} map
 * @param {string}          markerUrl
 * @param {HTMLElement|null} listEl
 * @param {Function}        onItemClick    Passed through to renderList.
 */
function showAllRetailers( allRetailers, center, radiusMiles, state, markerByKey, map, markerUrl, listEl, onItemClick ) {

	const filtered = filterAndSortRetailers( allRetailers, center, radiusMiles );

	applyResults( state, filtered, markerByKey, map, markerUrl, listEl, onItemClick );

}

/**
 * Render the searching / loading indicator.
 *
 * @param {HTMLElement} listEl
 */
function showLoadingState( listEl ) {

	listEl.innerHTML = `<p class="b-map__list-msg b-map__list-msg--loading">Searching…</p>`;

}

/**
 * Render the filtered retailers list.
 *
 * @param {HTMLElement}     listEl
 * @param {Array}           retailers   Filtered + sorted retailers with `distance`.
 * @param {Function}        onItemClick Called with (index, itemEl) when a row is clicked.
 */
function renderList( listEl, retailers, onItemClick ) {

	listEl.innerHTML = '';

	if ( ! retailers.length ) {

		listEl.innerHTML = `
			<p class="b-map__list-msg">
				No retailers found in this area. Try increasing the radius.
			</p>`;
		return;

	}

	retailers.forEach( ( retailer, i ) => {

		const number       = i + 1;
		const distanceText = retailer.distance !== undefined
			? `${ retailer.distance.toFixed( 2 ) } miles away`
			: '';

		const streetHtml   = retailer.street
			? `<span class="b-map__list-address-line">${ escapeHtml( retailer.street ) }</span>`
			: '';
		const localityHtml = retailer.locality
			? `<span class="b-map__list-address-line">${ escapeHtml( retailer.locality ) }</span>`
			: '';
		let addressHtml = '';
		if ( streetHtml || localityHtml ) {

			addressHtml = `<span class="b-map__list-address">${ streetHtml }${ localityHtml }</span>`;

		} else if ( retailer.address ) {

			addressHtml = `<span class="b-map__list-address"><span class="b-map__list-address-line">${ escapeHtml( retailer.address ) }</span></span>`;

		}

		const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${ encodeURIComponent( `${Number( retailer.latitude )  },${  Number( retailer.longitude )}` ) }`;

		const item           = document.createElement( 'div' );
		item.className       = 'b-map__list-item';
		item.setAttribute( 'role', 'listitem' );
		item.dataset.index   = String( i );

		item.innerHTML = `
			<button class="b-map__list-item-inner">
				<div class="b-map__list-pin${ digitsModifier( 'b-map__list-pin', number ) }" aria-hidden="true">
					<span class="b-map__list-pin-number">${ number }</span>
				</div>
				<div class="b-map__list-info">
					<strong class="b-map__list-name">${ escapeHtml( retailer.name ) }</strong>
					${ addressHtml }
					${ distanceText ? `<span class="b-map__list-distance">${ distanceText }</span>` : '' }
				</div>
			</button>
			<a
				class="b-map__list-directions"
				href="${ directionsUrl }"
				target="_blank"
				rel="noopener noreferrer nofollow"
				aria-label="Get directions to ${ escapeHtml( retailer.name ) }"
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22.18 10.5848L14.15 2.54984C13.37 1.76984 12.105 1.76984 11.32 2.54984L3.28495 10.5848C2.50495 11.3648 2.50495 12.6298 3.28495 13.4148L11.32 21.4498C12.1 22.2298 13.365 22.2298 14.15 21.4498L22.1849 13.4148C22.965 12.6348 22.965 11.3698 22.1849 10.5848H22.18ZM16.235 15.0548C16.235 13.4448 14.925 12.1348 13.315 12.1348H8.96495C10.95 13.2148 11.805 14.3848 11.85 14.4448L11.445 14.7348L11.04 15.0298C11.04 15.0298 9.90495 13.5048 7.22995 12.4148C6.90995 12.2848 6.70495 11.9748 6.70495 11.6298C6.70495 11.2848 6.90995 10.9798 7.22995 10.8448C9.90495 9.75984 11.03 8.24484 11.04 8.22984L11.85 8.81484C11.81 8.87484 10.95 10.0448 8.96495 11.1298H13.32C15.48 11.1298 17.24 12.8898 17.24 15.0498H16.24L16.235 15.0548Z" fill="white"/></svg>Get Directions
			</a>`;

		// Clicking the item row focuses the map marker.
		item.querySelector( '.b-map__list-item-inner' ).addEventListener( 'click', () => {

			onItemClick( i, item );

		} );

		listEl.appendChild( item );

	} );

}

// ─── Main block initialisation ────────────────────────────────────────────────

/**
 * Initialise a single map block.
 *
 * @param {HTMLElement} canvas  The `.b-map__canvas` element that carries data-* attributes.
 * @return {Promise<void>}
 */
async function initMapBlock( canvas ) {

	// Skip the placeholder rendered when no API key is configured.
	if ( canvas.classList.contains( 'b-map__canvas--no-api-key' ) ) {

		return;

	}

	const section     = canvas.closest( '.b-map' ) || canvas.parentElement;
	const listEl      = section ? section.querySelector( '.b-map__list' )         : null;
	const searchInput = section ? section.querySelector( '.b-map__search-input' ) : null;
	const radiusSelect = section ? section.querySelector( '.b-map__radius-select' ) : null;

	const markerUrl = canvas.dataset.markerUrl || '';
	const mapId     = ( canvas.dataset.mapId || '' ).trim();
	const centerLat    = parseFloat( canvas.dataset.mapLat    ) || 44.9778;
	const centerLng    = parseFloat( canvas.dataset.mapLng    ) || -93.2650;
	const zoom         = parseInt(   canvas.dataset.mapZoom, 10 ) || 9;
	const defaultRadius = parseFloat( canvas.dataset.mapRadius ) || 50;

	let allRetailers = [];
	try {

		allRetailers = JSON.parse( canvas.dataset.retailers || '[]' );

	} catch ( e ) {
		// Silently ignore malformed JSON – the map will render empty.
	}

	await waitForGoogleMaps();

	const { Map: GoogleMap, InfoWindow } = await window.google.maps.importLibrary( 'maps' );
	let AdvancedMarkerElement = null;

	const infoWindow = new InfoWindow();

	/** Current filtered set (full set shown in the list) and the retailer kept visible on the map. */
	const state = { filtered: [], indexByKey: new Map(), pinnedKey: null };

	/**
	 * Radius currently selected in the dropdown, falling back to the default.
	 *
	 * @return {number}
	 */
	const currentRadius = () => ( radiusSelect ? parseFloat( radiusSelect.value ) : NaN ) || defaultRadius;

	const mapOptions = {
		zoom,
		center:            { lat: centerLat, lng: centerLng },
		mapTypeControl:    false,
		streetViewControl: false,
		fullscreenControl: true,
		zoomControl:       true,
		styles:            MAP_STYLES,
	};

	if ( mapId ) {

		mapOptions.mapId = mapId;

	}

	const map = new GoogleMap( canvas, mapOptions );

	const hasValidMapId = Boolean( map.get( 'mapId' ) );

	if ( hasValidMapId ) {

		( { AdvancedMarkerElement } = await window.google.maps.importLibrary( 'marker' ) );

	}

	const useAdvancedMarkers = hasValidMapId
		&& Boolean( map.getMapCapabilities?.().isAdvancedMarkersAvailable )
		&& Boolean( AdvancedMarkerElement );

	// Use AdvancedMarkerElement only when map capabilities support it.
	// Markers start detached; syncMarkers() attaches the ones that should be visible.
	const markerObjects = allRetailers
		.filter( r => r.latitude !== null && r.latitude !== undefined && r.longitude !== null && r.longitude !== undefined )
		.map( retailer => {

			const marker = useAdvancedMarkers
				? new AdvancedMarkerElement( {
					position: { lat: Number( retailer.latitude ), lng: Number( retailer.longitude ) },
					title:   retailer.name,
				} )
				: new window.google.maps.Marker( {
					position: { lat: Number( retailer.latitude ), lng: Number( retailer.longitude ) },
					title: retailer.name,
				} );

			// Clicking a map marker opens the InfoWindow and highlights the list row.
			marker.addListener( useAdvancedMarkers ? 'gmp-click' : 'click', () => {

				const addressLine = retailer.address
					? `<p class="b-map__info-address">${ escapeHtml( retailer.address ) }</p>`
					: '';
				const phoneLine = retailer.phone
					? `<p class="b-map__info-phone">${ escapeHtml( retailer.phone ) }</p>`
					: '';
				const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${ encodeURIComponent( `${ Number( retailer.latitude ) },${ Number( retailer.longitude ) }` ) }`;
				const directionsBtn = `<a
				class="b-map__list-directions"
				href="${ directionsUrl }"
				target="_blank"
				rel="noopener noreferrer nofollow"
				aria-label="Get directions to ${ escapeHtml( retailer.name ) }"
			><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22.18 10.5848L14.15 2.54984C13.37 1.76984 12.105 1.76984 11.32 2.54984L3.28495 10.5848C2.50495 11.3648 2.50495 12.6298 3.28495 13.4148L11.32 21.4498C12.1 22.2298 13.365 22.2298 14.15 21.4498L22.1849 13.4148C22.965 12.6348 22.965 11.3698 22.1849 10.5848H22.18ZM16.235 15.0548C16.235 13.4448 14.925 12.1348 13.315 12.1348H8.96495C10.95 13.2148 11.805 14.3848 11.85 14.4448L11.445 14.7348L11.04 15.0298C11.04 15.0298 9.90495 13.5048 7.22995 12.4148C6.90995 12.2848 6.70495 11.9748 6.70495 11.6298C6.70495 11.2848 6.90995 10.9798 7.22995 10.8448C9.90495 9.75984 11.03 8.24484 11.04 8.22984L11.85 8.81484C11.81 8.87484 10.95 10.0448 8.96495 11.1298H13.32C15.48 11.1298 17.24 12.8898 17.24 15.0498H16.24L16.235 15.0548Z" fill="white"/></svg>Get Directions</a>`;

				infoWindow.setContent(
					`<div class="b-map__info-window">
				<strong class="b-map__info-name">${ escapeHtml( retailer.name ) }</strong>
				${ addressLine }${ phoneLine }
				${ directionsBtn }
			</div>`
				);
				state.pinnedKey = retailerKey( retailer );
				infoWindow.open( {
					map,
					anchor: marker,
				} );

				// Highlight the matching list row.
				if ( listEl ) {

					listEl.querySelectorAll( '.b-map__list-item' ).forEach( el => el.classList.remove( 'is-active' ) );
					const match = listEl.querySelector( `.b-map__list-item[data-index="${ state.indexByKey.get( state.pinnedKey ) }"]` );
					if ( match ) {

						match.classList.add( 'is-active' );
						// Instant scroll: with content-visibility, rows rendered mid-animation change height and shift the target.
						match.scrollIntoView( { block: 'nearest', behavior: 'instant' } );

					}

				}

			} );

			return { retailer, marker, number: null };

		} );

	// ── Build O(1) marker index ───────────────────────────────────────────────
	// Keyed by retailerKey() so every subsequent lookup (filter, click, highlight)
	// is O(1) instead of O(n) via Array.find().
	/** @type {Map<string, {retailer: object, marker: object, number: number|null}>} */
	const markerByKey = new Map(
		markerObjects.map( mo => [ retailerKey( mo.retailer ), mo ] )
	);

	/**
	 * Re-select which markers are visible for the current filter and viewport.
	 *
	 * @return {void}
	 */
	const syncVisibleMarkers = () => syncMarkers( state, markerByKey, map, markerUrl );

	// Re-select visible pins after every zoom / pan, and release the pin when the InfoWindow is closed.
	map.addListener( 'idle', syncVisibleMarkers );
	infoWindow.addListener( 'closeclick', () => {

		state.pinnedKey = null;
		syncVisibleMarkers();

	} );

	// ── Show all retailers on initial load ───────────────────────────────────
	// Build the click handler closure here so we can reuse it on search reset.
	/**
	 * Handle list item click: highlights the row and opens the InfoWindow.
	 *
	 * @param {number}      index  Zero-based index of the clicked retailer.
	 * @param {HTMLElement} itemEl The clicked list item element.
	 */
	function onListItemClick( index, itemEl ) {

		if ( ! listEl ) {

			return;

		}

		listEl.querySelectorAll( '.b-map__list-item' ).forEach( el => el.classList.remove( 'is-active' ) );
		itemEl.classList.add( 'is-active' );

		const currentRetailers = state.filtered;
		const retailer         = currentRetailers[ index ];
		if ( ! retailer ) {

			return;

		}

		const mo = markerByKey.get( retailerKey( retailer ) );

		if ( mo ) {

			// Keep the marker on the map while zooming, even if it was thinned out.
			state.pinnedKey = retailerKey( retailer );
			syncVisibleMarkers();

			map.panTo( { lat: Number( retailer.latitude ), lng: Number( retailer.longitude ) } );
			map.setZoom( 15 );

			const addressLine = retailer.address
				? `<p class="b-map__info-address">${ escapeHtml( retailer.address ) }</p>`
				: '';
			const phoneLine = retailer.phone
				? `<p class="b-map__info-phone">${ escapeHtml( retailer.phone ) }</p>`
				: '';
			const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${ encodeURIComponent( `${ Number( retailer.latitude ) },${ Number( retailer.longitude ) }` ) }`;
			const directionsBtn = `<a
			class="b-map__list-directions"
			href="${ directionsUrl }"
			target="_blank"
			rel="noopener noreferrer nofollow"
			aria-label="Get directions to ${ escapeHtml( retailer.name ) }"
		><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22.18 10.5848L14.15 2.54984C13.37 1.76984 12.105 1.76984 11.32 2.54984L3.28495 10.5848C2.50495 11.3648 2.50495 12.6298 3.28495 13.4148L11.32 21.4498C12.1 22.2298 13.365 22.2298 14.15 21.4498L22.1849 13.4148C22.965 12.6348 22.965 11.3698 22.1849 10.5848H22.18ZM16.235 15.0548C16.235 13.4448 14.925 12.1348 13.315 12.1348H8.96495C10.95 13.2148 11.805 14.3848 11.85 14.4448L11.445 14.7348L11.04 15.0298C11.04 15.0298 9.90495 13.5048 7.22995 12.4148C6.90995 12.2848 6.70495 11.9748 6.70495 11.6298C6.70495 11.2848 6.90995 10.9798 7.22995 10.8448C9.90495 9.75984 11.03 8.24484 11.04 8.22984L11.85 8.81484C11.81 8.87484 10.95 10.0448 8.96495 11.1298H13.32C15.48 11.1298 17.24 12.8898 17.24 15.0498H16.24L16.235 15.0548Z" fill="white"/></svg>Get Directions</a>`;

			infoWindow.setContent(
				`<div class="b-map__info-window">
				<strong class="b-map__info-name">${ escapeHtml( retailer.name ) }</strong>
				${ addressLine }${ phoneLine }
				${ directionsBtn }
			</div>`
			);
			infoWindow.open( {
				map,
				anchor: mo.marker,
			} );

		}

	}

	showAllRetailers( allRetailers, { lat: centerLat, lng: centerLng }, currentRadius(), state, markerByKey, map, markerUrl, listEl, onListItemClick );

	// ── Search handler (only wired when controls are present) ─────────────────
	if ( searchInput && radiusSelect ) {

		let debounceTimer;

		/**
		 *
		 */
		async function handleSearch() {

			const query  = searchInput.value.trim();
			const radius = currentRadius();

			if ( ! query ) {

				// Reset – show retailers around the default centre (same as initial load).
				const defaultCenter = { lat: centerLat, lng: centerLng };
				fitMapToRadius( map, defaultCenter, radius );
				showAllRetailers( allRetailers, defaultCenter, radius, state, markerByKey, map, markerUrl, listEl, onListItemClick );
				return;

			}

			// Only the event is sent; the entered ZIP/city/address never reaches analytics.
			window.MNLAnalytics?.track( 'retailer_search', getParameters( section ) );

			if ( listEl ) {

				showLoadingState( listEl );

			}

			try {

				const center   = await geocodeAddress( query );
				const filtered = filterAndSortRetailers( allRetailers, center, radius );

				applyResults( state, filtered, markerByKey, map, markerUrl, listEl, onListItemClick );
				fitMapToRadius( map, center, radius );

			} catch ( e ) {

				if ( listEl ) {

					listEl.innerHTML = `
					<p class="b-map__list-msg b-map__list-msg--error">
						Could not find that location. Please try a different ZIP code or city.
					</p>`;

				}

			}

		}

		searchInput.addEventListener( 'input', () => {

			clearTimeout( debounceTimer );
			debounceTimer = setTimeout( handleSearch, 600 );

		} );

		searchInput.addEventListener( 'keydown', ( e ) => {

			if ( e.key === 'Enter' ) {

				clearTimeout( debounceTimer );
				handleSearch();

			}

		} );

		radiusSelect.addEventListener( 'change', () => {

			clearTimeout( debounceTimer );
			handleSearch();

		} );

	} // end if ( searchInput && radiusSelect )

}

// ── Boot ──────────────────────────────────────────────────────────────────────
document.querySelectorAll( '.b-map__canvas' ).forEach( canvas => {

	initMapBlock( canvas );

} );
